// Exceeds target ceiling — single responsibility justified (terms acceptance modal with versioned content)
import { useState, useEffect } from 'react'
import { Building2, Shield, CheckCircle, ArrowLeft, X } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { PrivacyContent, TermsContent } from './LegalContent'

interface TermsAcceptanceModalProps {
  onAccept: () => void
  userType: 'landlord' | 'agent' | 'tenant' | 'contractor' | 'prospect'
}

type LegalView = 'welcome' | 'terms' | 'privacy'

export default function TermsAcceptanceModal({ onAccept, userType }: TermsAcceptanceModalProps) {
  const { user } = useAuth()
  const [accepting, setAccepting] = useState(false)
  const [error, setError] = useState('')
  const [view, setView] = useState<LegalView>('welcome')

  const handleAccept = async () => {
    if (!user) return

    setAccepting(true)
    setError('')

    try {
      // Record the acceptance in the database with timestamp and metadata
      const { error: insertError } = await supabase
        .from('terms_acceptances')
        .insert({
          user_id: user.id,
          user_type: userType === 'prospect' ? 'tenant' : userType,
          accepted_at: new Date().toISOString(),
          ip_address: null, // Would be captured server-side for security
          user_agent: navigator.userAgent,
          terms_version: '2026-10-18', // Version of terms being accepted
        })

      if (insertError) {
        console.error('Failed to record terms acceptance:', insertError)
      }

      // Store in localStorage as a quick check for subsequent page loads
      localStorage.setItem(`lentals_terms_accepted_${user.id}`, new Date().toISOString())

      onAccept()
    } catch (err) {
      setError('Failed to record acceptance. Please try again.')
      console.error('Terms acceptance error:', err)
    } finally {
      setAccepting(false)
    }
  }

  const userTypeLabels = {
    landlord: 'Lentals',
    agent: 'Lentals',
    tenant: 'Tenant Hub',
    contractor: 'Contractor Portal',
    prospect: 'Prospect Hub',
  }

  // Legal content popout view
  if (view === 'terms' || view === 'privacy') {
    return (
      <div className="fixed inset-0 flex items-center justify-center p-4 z-50" style={{ backgroundColor: 'rgba(26, 39, 68, 0.95)' }}>
        <div
          className="rounded-2xl max-w-lg w-full flex flex-col"
          style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-color)', maxHeight: 'min(85vh, 700px)' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 sm:px-6 py-4 flex-shrink-0" style={{ borderBottom: '1px solid var(--border-color)' }}>
            <button
              onClick={() => setView('welcome')}
              className="flex items-center gap-1.5 text-sm font-medium hover:underline"
              style={{ color: 'var(--color-accent)' }}
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <h3 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>
              {view === 'terms' ? 'Terms of Service' : 'Privacy Policy'}
            </h3>
            <button
              onClick={() => setView('welcome')}
              className="p-1 rounded-lg transition hover:bg-black/5"
              style={{ color: 'var(--text-muted)' }}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Scrollable content */}
          <div className="overflow-y-auto px-4 sm:px-6 py-5 flex-1" style={{ color: 'var(--text-secondary)' }}>
            {view === 'terms' ? <TermsContent /> : <PrivacyContent />}
          </div>

          {/* Footer */}
          <div className="px-4 sm:px-6 py-4 flex-shrink-0" style={{ borderTop: '1px solid var(--border-color)' }}>
            <button
              onClick={() => setView('welcome')}
              className="btn-primary w-full py-2.5 font-semibold rounded-xl transition"
            >
              Back to continue
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Welcome view
  return (
    <div className="fixed inset-0 flex items-center justify-center p-4 z-50" style={{ backgroundColor: 'rgba(26, 39, 68, 0.95)' }}>
      <div className="rounded-2xl p-8 max-w-md w-full text-center" style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
        {/* Logo */}
        <div className="inline-flex items-center gap-2 mb-6">
          <Building2 className="w-8 h-8" style={{ color: 'var(--color-accent)' }} />
          <span className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Lentals</span>
        </div>

        {/* Welcome message */}
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'color-mix(in srgb, var(--color-accent) 10%, transparent)' }}>
          <Shield className="w-8 h-8" style={{ color: 'var(--color-accent)' }} />
        </div>

        <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
          Welcome to {userTypeLabels[userType]}
        </h2>

        <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
          {(userType === 'landlord' || userType === 'agent') && 'Manage your properties, tenants, compliance, and finances — all in one place.'}
          {userType === 'tenant' && 'Access your tenancy documents, report maintenance issues, and communicate with your landlord.'}
          {userType === 'contractor' && 'Manage your jobs, submit quotes, and communicate with property managers.'}
          {userType === 'prospect' && 'View property details, schedule viewings, and track your application.'}
        </p>

        {/* Features highlight */}
        <div className="rounded-xl p-4 mb-6 text-left" style={{ backgroundColor: 'var(--bg-surface)' }}>
          <p className="text-sm font-medium mb-3" style={{ color: 'var(--text-primary)' }}>What you can do:</p>
          <ul className="space-y-2">
            {(userType === 'landlord' || userType === 'agent') && (
              <>
                <li className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-success, #22c55e)' }} />
                  Track rent, compliance, and maintenance
                </li>
                <li className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-success, #22c55e)' }} />
                  Onboard tenants and serve documents
                </li>
                <li className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-success, #22c55e)' }} />
                  Full audit trail for legal compliance
                </li>
              </>
            )}
            {userType === 'tenant' && (
              <>
                <li className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-success, #22c55e)' }} />
                  View and acknowledge tenancy documents
                </li>
                <li className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-success, #22c55e)' }} />
                  Report and track maintenance issues
                </li>
                <li className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-success, #22c55e)' }} />
                  Communicate securely with your landlord
                </li>
              </>
            )}
            {userType === 'contractor' && (
              <>
                <li className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-success, #22c55e)' }} />
                  View and accept job assignments
                </li>
                <li className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-success, #22c55e)' }} />
                  Submit quotes and track progress
                </li>
                <li className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-success, #22c55e)' }} />
                  Communicate with landlords and tenants
                </li>
              </>
            )}
            {userType === 'prospect' && (
              <>
                <li className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-success, #22c55e)' }} />
                  View property details and photos
                </li>
                <li className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-success, #22c55e)' }} />
                  Schedule and manage viewings
                </li>
                <li className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-success, #22c55e)' }} />
                  Submit documents for referencing
                </li>
              </>
            )}
          </ul>
        </div>

        {error && (
          <p className="text-sm mb-4" style={{ color: 'var(--color-error, #ef4444)' }}>{error}</p>
        )}

        {/* Accept button */}
        <button
          onClick={handleAccept}
          disabled={accepting}
          className="btn-primary w-full py-3 font-semibold rounded-xl transition disabled:opacity-50"
        >
          {accepting ? 'Please wait...' : 'Accept & Continue'}
        </button>

        {/* Terms link */}
        <p className="mt-4 text-xs" style={{ color: 'var(--text-muted)' }}>
          By continuing you agree to our{' '}
          <button
            onClick={() => setView('terms')}
            className="underline cursor-pointer"
            style={{ color: 'var(--color-accent)' }}
          >
            Terms of Service
          </button>
          {' '}and{' '}
          <button
            onClick={() => setView('privacy')}
            className="underline cursor-pointer"
            style={{ color: 'var(--color-accent)' }}
          >
            Privacy Policy
          </button>
        </p>
      </div>
    </div>
  )
}

/**
 * Hook to check if user has accepted terms.
 * Checks localStorage first (fast), then falls back to a database query.
 * Returns true if accepted, false if not, null while checking.
 */
export function useTermsAccepted(userId: string | undefined): boolean | null {
  const [accepted, setAccepted] = useState<boolean | null>(() => {
    if (!userId) return null
    // Fast path: check localStorage
    if (localStorage.getItem(`lentals_terms_accepted_${userId}`)) return true
    // Need to check database — return null (loading)
    return null
  })

  useEffect(() => {
    if (!userId) return
    // If already resolved from localStorage, skip DB check
    if (localStorage.getItem(`lentals_terms_accepted_${userId}`)) {
      setAccepted(true)
      return
    }

    let cancelled = false

    async function checkDatabase() {
      // Check terms_acceptances table
      const { data: acceptance } = await supabase
        .from('terms_acceptances')
        .select('accepted_at')
        .eq('user_id', userId)
        .order('accepted_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (!cancelled && acceptance?.accepted_at) {
        localStorage.setItem(`lentals_terms_accepted_${userId}`, acceptance.accepted_at)
        setAccepted(true)
        return
      }

      // Neither source has acceptance — user hasn't accepted yet
      if (!cancelled) {
        setAccepted(false)
      }
    }

    checkDatabase()

    return () => { cancelled = true }
  }, [userId])

  return accepted
}
